import React, { useState, useRef, memo } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { MountainSnowIcon, ClockIcon, CalendarIcon, ArrowUpIcon, BedDoubleIcon, UtensilsIcon, FootprintsIcon, SunIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon, XIcon, StarIcon, ThermometerIcon, MapPinIcon, PhoneIcon, HelpCircleIcon, MailIcon, UserIcon, MessageSquareIcon } from 'lucide-react';
interface EverestTrekPageProps {
  onNavigate: (page: string) => void;
}
/* ═══════════════════════════════════════════
   TOUR HERO BANNER
   ═══════════════════════════════════════════ */
function TourHero({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <section className="relative w-full min-h-[70vh] flex items-end overflow-hidden" data-id="element-898">
      <div className="absolute inset-0" data-id="element-899">
        <img src="https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=1920&q=80" alt="Mount Everest and Himalayan peaks at sunrise" className="w-full h-full object-cover" data-id="element-900" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" data-id="element-901" />
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-32" data-id="element-902">
        <nav className="mb-6" aria-label="Breadcrumb" data-id="element-903">
          <ol className="flex items-center gap-2 text-sm text-white/60" data-id="element-904">
            <li data-id="element-905">
              <button onClick={() => onNavigate('home')} className="hover:text-white transition-colors" data-id="element-906">
                Home
              </button>
            </li>
            <li data-id="element-907">/</li>
            <li data-id="element-908">
              <button onClick={() => onNavigate('home')} className="hover:text-white transition-colors" data-id="element-909">
                Luxury Treks
              </button>
            </li>
            <li data-id="element-910">/</li>
            <li className="text-[#D4A853]" data-id="element-911">Everest Base Camp</li>
          </ol>
        </nav>

        <motion.h1 initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 max-w-3xl" data-id="element-912">
          Everest Base Camp
          <br data-id="element-913" />
          <span className="italic font-medium text-[#D4A853]" data-id="element-914">Luxury Trek</span>
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.15
      }} className="text-white/70 text-lg max-w-2xl mb-8 font-light" data-id="element-915">
          A 14-day premium journey to the foot of the world's highest peak,
          blending adventure with unparalleled comfort.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} className="flex flex-wrap gap-6 sm:gap-10" data-id="element-916">
          <div className="flex items-center gap-2.5" data-id="element-917">
            <CalendarIcon className="w-5 h-5 text-[#D4A853]" data-id="element-918" />
            <div data-id="element-919">
              <p className="text-white text-sm font-semibold" data-id="element-920">14 Days</p>
              <p className="text-white/50 text-xs" data-id="element-921">Duration</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5" data-id="element-922">
            <ArrowUpIcon className="w-5 h-5 text-[#D4A853]" data-id="element-923" />
            <div data-id="element-924">
              <p className="text-white text-sm font-semibold" data-id="element-925">5,364m</p>
              <p className="text-white/50 text-xs" data-id="element-926">Max Elevation</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5" data-id="element-927">
            <FootprintsIcon className="w-5 h-5 text-[#D4A853]" data-id="element-928" />
            <div data-id="element-929">
              <p className="text-white text-sm font-semibold" data-id="element-930">5–7 hrs/day</p>
              <p className="text-white/50 text-xs" data-id="element-931">Walking</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5" data-id="element-932">
            <SunIcon className="w-5 h-5 text-[#D4A853]" data-id="element-933" />
            <div data-id="element-934">
              <p className="text-white text-sm font-semibold" data-id="element-935">Sep – May</p>
              <p className="text-white/50 text-xs" data-id="element-936">Best Season</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ═══════════════════════════════════════════
   TRIP HIGHLIGHTS
   ═══════════════════════════════════════════ */
function TripHighlights() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const highlights = ["Stand at the foot of the world's highest peak at Everest Base Camp (5,364m)", 'Witness a breathtaking sunrise panorama from Kala Patthar (5,545m)', "Fly the legendary Lukla route — one of the world's most thrilling flights", 'Stay in premium luxury lodges and 5-star hotels throughout the journey', 'Visit the iconic Tengboche Monastery, the largest in the Khumbu region', 'Cross the famous Hillary Suspension Bridge over the Dudh Koshi river', 'Explore Namche Bazaar — the vibrant Sherpa capital and trading hub', 'Experience Sherpa culture, cuisine, and hospitality firsthand', 'Trek through rhododendron forests, alpine meadows, and glacial moraines', 'Enjoy full board gourmet meals prepared by skilled chefs daily'];
  return <section className="w-full bg-[#FAF8F5] pb-20 sm:pb-28" ref={ref} data-id="element-937">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-938">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="mb-8" data-id="element-939">
          <h3 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-id="element-940">
            Trip Highlights
          </h3>
          <p className="text-slate-500 font-light" data-id="element-941">
            What makes this journey unforgettable
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3.5" data-id="element-942">
          {highlights.map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -12
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.4,
          delay: Math.min(i * 0.06, 0.5)
        }} className="flex items-start gap-3" data-id="element-943">
              <CheckIcon className="w-4 h-4 text-teal-600 mt-1 flex-shrink-0" data-id="element-944" />
              <span className="text-slate-700 text-sm leading-relaxed" data-id="element-945">
                {item}
              </span>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
/* ═══════════════════════════════════════════
   TRIP OVERVIEW (Concise ~250 words)
   ═══════════════════════════════════════════ */
function TripOverview() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-[#FAF8F5] py-20 sm:py-28" ref={ref} data-id="element-946">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-947">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} data-id="element-948">
          <div className="flex items-center gap-3 mb-6" data-id="element-949">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-950">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-951" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-952">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-6" data-id="element-953">
            Trip Overview
          </h2>
          <div className="space-y-4 text-slate-600 leading-relaxed text-base" data-id="element-954">
            <p data-id="element-955">
              The Everest Base Camp Luxury Trek is our signature 14-day
              expedition through the legendary Khumbu Valley to the foot of
              Mount Everest at 5,364 meters. Unlike standard treks, every detail
              has been elevated to provide five-star comfort in the world's most
              dramatic landscape — from premium lodges and gourmet meals to
              experienced Sherpa guides who know every trail intimately.
            </p>
            <p data-id="element-956">
              Beginning with a scenic flight from Kathmandu to Lukla, you'll
              traverse ancient Sherpa villages, cross suspension bridges over
              roaring rivers, and pass through rhododendron forests. Two
              acclimatization days in Namche Bazaar and Dingboche ensure safe
              altitude adjustment. The journey culminates at Everest Base Camp,
              followed by a breathtaking sunrise from Kala Patthar (5,545m) —
              offering the most spectacular panoramic views of Everest, Lhotse,
              Nuptse, and the surrounding peaks.
            </p>
            <p data-id="element-957">
              Throughout the trek, you'll stay in the finest lodges available,
              enjoy full board gourmet meals, and travel in small groups of no
              more than 12 for a personalized experience. This is more than a
              trek — it's a transformative journey combining physical
              achievement with cultural immersion and luxurious comfort.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ═══════════════════════════════════════════
   DAY-TO-DAY ITINERARY (Full-width, more descriptive)
   ═══════════════════════════════════════════ */
interface ItineraryDay {
  day: number;
  title: string;
  elevation: string;
  hours: string;
  hotel: string;
  description: string;
  meals: string;
}
const itineraryData: ItineraryDay[] = [{
  day: 1,
  title: 'Arrival in Kathmandu',
  elevation: '1,400m',
  hours: '—',
  hotel: "Dwarika's Hotel",
  meals: 'Welcome Dinner',
  description: "Welcome to Nepal! Upon arrival at Tribhuvan International Airport, you'll be greeted by our representative and transferred in a private luxury vehicle to the heritage Dwarika's Hotel — a masterpiece of Newari architecture and one of Asia's finest boutique hotels. After settling in, enjoy a leisurely afternoon exploring the hotel's stunning courtyards and spa facilities. In the evening, join your trek leader and fellow trekkers for a welcome dinner where you'll receive a comprehensive briefing about the journey ahead, including gear check and altitude preparation tips."
}, {
  day: 2,
  title: 'Kathmandu Cultural Exploration',
  elevation: '1,400m',
  hours: '3–4 hrs',
  hotel: "Dwarika's Hotel",
  meals: 'Breakfast, Lunch, Dinner',
  description: "A full day immersed in the cultural treasures of the Kathmandu Valley. Begin with a guided visit to the magnificent Boudhanath Stupa — one of the largest Buddhist stupas in the world, where you'll witness monks performing morning prayers. Continue to the sacred Hindu temple of Pashupatinath on the banks of the Bagmati River, observing ancient cremation rituals. After a gourmet lunch at a rooftop restaurant overlooking the city, explore the medieval architecture of Kathmandu Durbar Square. Return to the hotel for a final gear check, equipment fitting, and packing session with your guide. Evening at leisure to rest before the trek begins."
}, {
  day: 3,
  title: 'Fly to Lukla & Trek to Phakding',
  elevation: '2,610m',
  hours: '3–4 hrs',
  hotel: 'Luxury Lodge',
  meals: 'Breakfast, Lunch, Dinner',
  description: "An early morning transfer to the domestic airport for the legendary 35-minute flight to Lukla (2,860m) — often described as one of the world's most thrilling flights, with dramatic mountain views and a short runway perched on a mountainside. Upon landing, meet your full trekking team including porters and assistant guides. Begin your trek descending gently through terraced farmland and pine forests, following the Dudh Koshi (Milk River) valley. Cross several suspension bridges adorned with prayer flags as you pass through small Sherpa settlements. Arrive at the peaceful village of Phakding, nestled along the riverbank, where your luxury lodge awaits with hot showers and a warm dining room."
}, {
  day: 4,
  title: 'Trek to Namche Bazaar',
  elevation: '3,440m',
  hours: '5–6 hrs',
  hotel: 'Everest View Hotel',
  meals: 'Breakfast, Lunch, Dinner',
  description: "Today's trek is one of the most rewarding of the entire journey. Follow the Dudh Koshi valley through dense forests of blue pine and rhododendron, crossing the river multiple times on high suspension bridges — including the famous Hillary Suspension Bridge, one of the longest in the Khumbu region. After entering Sagarmatha National Park (a UNESCO World Heritage Site), begin the steep ascent to Namche Bazaar. Along the way, catch your first breathtaking glimpse of Mount Everest towering above the Lhotse-Nuptse ridge. Arrive at Namche Bazaar, the bustling Sherpa capital and trading hub, and check into the legendary Everest View Hotel — the world's highest-altitude luxury hotel with panoramic mountain views from every room."
}, {
  day: 5,
  title: 'Acclimatization Day in Namche Bazaar',
  elevation: '3,440m',
  hours: '4–5 hrs',
  hotel: 'Everest View Hotel',
  meals: 'Breakfast, Lunch, Dinner',
  description: "A crucial acclimatization day designed to help your body adjust to the altitude before climbing higher. The golden rule of high-altitude trekking is 'climb high, sleep low,' so today you'll hike up to the Everest View Hotel viewpoint at 3,880m for a stunning 360-degree panorama of Everest, Ama Dablam, Thamserku, and Kongde Ri. Visit the fascinating Sherpa Culture Museum to learn about the history, traditions, and mountaineering legacy of the Sherpa people. Explore the vibrant Namche market where traders from Tibet and the lowlands exchange goods. Optional afternoon visit to the nearby village of Khumjung to see the famous Yeti scalp at the local monastery. Return to the hotel for a restful evening with hot drinks and mountain views."
}, {
  day: 6,
  title: 'Trek to Tengboche',
  elevation: '3,860m',
  hours: '5–6 hrs',
  hotel: 'Luxury Lodge',
  meals: 'Breakfast, Lunch, Dinner',
  description: "Descend from Namche through fragrant juniper and rhododendron forests to the Dudh Koshi river, then begin a gradual climb through one of the most beautiful stretches of the trek. The trail winds through ancient forests alive with Himalayan birds — look for the colorful Danphe (Impeyan pheasant), Nepal's national bird. As you ascend, the views become increasingly dramatic with Ama Dablam dominating the skyline like a giant ice-draped sentinel. Arrive at Tengboche, home to the largest and most important Buddhist monastery in the Khumbu region. If timing permits, attend the evening prayer ceremony with monks chanting in the candlelit prayer hall — a profoundly moving experience. Your luxury lodge sits on a ridge with unobstructed views of Everest, Nuptse, Lhotse, and Ama Dablam."
}, {
  day: 7,
  title: 'Trek to Dingboche',
  elevation: '4,410m',
  hours: '5–6 hrs',
  hotel: 'Luxury Lodge',
  meals: 'Breakfast, Lunch, Dinner',
  description: 'Leave the tree line behind as you enter the stark, high-altitude landscape of the upper Khumbu. The trail descends through the last rhododendron forest to cross the Imja Khola river, then climbs steadily through alpine scrub and yak pastures. The air is noticeably thinner now, and the pace slows accordingly. Pass through the small settlement of Pangboche, home to the oldest monastery in the Khumbu and believed to house a genuine Yeti relic. Continue ascending through a wide, wind-swept valley with towering peaks on all sides — Island Peak, Lhotse, and Ama Dablam create an amphitheater of ice and rock. Arrive at Dingboche, a farming village where potatoes and barley grow in stone-walled fields at over 4,400 meters. Your heated lodge provides welcome warmth against the evening chill.'
}, {
  day: 8,
  title: 'Acclimatization Day in Dingboche',
  elevation: '4,410m',
  hours: '4–5 hrs',
  hotel: 'Luxury Lodge',
  meals: 'Breakfast, Lunch, Dinner',
  description: "Your second acclimatization day is essential preparation for the highest altitudes ahead. After a hearty breakfast, hike up to Nagarjun Hill (5,100m) — a challenging but rewarding climb that offers some of the most spectacular views of the entire trek. From the summit ridge, gaze upon the massive south face of Lhotse (8,516m), the pyramid of Makalu (8,485m) — the world's fifth highest peak, and the striking form of Island Peak (6,189m). Your guide will explain the mountaineering routes visible on these peaks. Descend back to Dingboche for a late lunch and spend the afternoon resting, reading, or journaling. Your guide will conduct health checks including oxygen saturation monitoring to ensure everyone is acclimatizing well. Enjoy a warm dinner and early bedtime to prepare for the days ahead."
}, {
  day: 9,
  title: 'Trek to Lobuche',
  elevation: '4,940m',
  hours: '5–6 hrs',
  hotel: 'Luxury Lodge',
  meals: 'Breakfast, Lunch, Dinner',
  description: "Trek through the increasingly barren and dramatic high-altitude landscape toward the Khumbu Glacier. The trail climbs steadily past Dughla (Thukla), where you'll stop for tea at a small teahouse before ascending the steep Thukla Pass. At the top, pause at the Chukpo Lari memorial area — a solemn collection of stone cairns and prayer flags honoring climbers who lost their lives on Everest and the surrounding peaks, including memorials to Scott Fischer and other legendary mountaineers. Continue along the lateral moraine of the Khumbu Glacier, one of the world's great rivers of ice. The views of Pumori (7,161m), Nuptse's massive west face, and the Changri Nup glacier are extraordinary. Arrive at the small settlement of Lobuche, where your lodge provides shelter at nearly 5,000 meters. The night sky at this altitude is unforgettable — millions of stars in crystal-clear air."
}, {
  day: 10,
  title: 'Trek to Gorak Shep & Everest Base Camp',
  elevation: '5,364m',
  hours: '7–8 hrs',
  hotel: 'Luxury Lodge',
  meals: 'Breakfast, Lunch, Dinner',
  description: "The day you've been dreaming of. Rise early and trek across the Khumbu Glacier's rocky moraine to Gorak Shep (5,164m), a frozen lakebed that serves as the last settlement before Base Camp. Drop your bags at the lodge, have a quick lunch, then set out for Everest Base Camp itself. The 2-hour walk follows the glacier's edge, weaving between ice seracs and boulders. As you approach Base Camp (5,364m), the scale of the Khumbu Icefall becomes apparent — a chaotic frozen cascade of ice towers and crevasses that climbers must navigate to reach the higher camps. During climbing season, the camp buzzes with colorful tents and expedition teams. Stand at the foot of the world's highest mountain and let the enormity of your achievement sink in. Celebrate with your team, take photos, and soak in the atmosphere before returning to Gorak Shep as the afternoon light paints the peaks in gold and pink."
}, {
  day: 11,
  title: 'Kala Patthar Sunrise & Trek to Pheriche',
  elevation: '5,545m → 4,371m',
  hours: '7–8 hrs',
  hotel: 'Luxury Lodge',
  meals: 'Breakfast, Lunch, Dinner',
  description: "Set your alarm for 4:00 AM — this morning's pre-dawn hike to Kala Patthar (5,545m) is the absolute highlight of the trek. Climb for approximately 90 minutes in the dark, guided by headlamp and starlight, to reach the famous viewpoint just as the first rays of sun illuminate the summit of Everest in brilliant gold and orange. The 360-degree panorama from Kala Patthar is simply the finest mountain view on Earth — Everest, Lhotse, Nuptse, Pumori, Changtse, and dozens of other peaks glow in the dawn light while the Khumbu Glacier stretches below like a frozen highway. After extensive photography and quiet contemplation, descend to Gorak Shep for a celebratory breakfast. Continue the descent through increasingly green and oxygen-rich terrain to Pheriche (4,371m), enjoying the ease of downhill walking and the satisfaction of having reached your goal."
}, {
  day: 12,
  title: 'Trek to Namche Bazaar',
  elevation: '3,440m',
  hours: '6–7 hrs',
  hotel: 'Everest View Hotel',
  meals: 'Breakfast, Lunch, Dinner',
  description: "A long but exhilarating descent retracing your steps through landscapes that feel entirely different on the return journey. With the pressure of altitude behind you, there's time to notice details you missed on the way up — wildflowers, mani stones carved with prayers, yak herders moving their animals between pastures. Pass through Tengboche for a final look at the monastery and its incredible mountain backdrop. Continue descending through forests that feel luxuriously green and oxygen-rich after days above the tree line. Arrive back in Namche Bazaar for a final evening at the Everest View Hotel. Tonight's celebratory dinner is a special affair — your guide will present certificates of achievement and share stories from the trail. Browse the Namche shops for souvenirs and gifts."
}, {
  day: 13,
  title: 'Trek to Lukla',
  elevation: '2,860m',
  hours: '6–7 hrs',
  hotel: 'Luxury Lodge',
  meals: 'Breakfast, Lunch, Dinner',
  description: "Your final day on the trail. Descend from Namche through the familiar forests and river crossings, savoring every last moment of the Khumbu Valley. The trail feels easier now — your legs are strong, your lungs are adapted, and there's a lightness that comes from accomplishment. Pass through the villages of Phakding and Chheplung, waving to familiar faces along the way. Arrive in Lukla by mid-afternoon with time to explore this remarkable mountain town. In the evening, gather with your Sherpa team, porters, and fellow trekkers for a heartfelt farewell dinner. This is a deeply emotional occasion — share stories, exchange contact details, and express gratitude to the incredible team who made your journey possible. Traditional Sherpa songs and dances often accompany the celebration."
}, {
  day: 14,
  title: 'Fly to Kathmandu & Departure',
  elevation: '1,400m',
  hours: '—',
  hotel: "Dwarika's Hotel",
  meals: 'Breakfast, Farewell Dinner',
  description: "Morning flight back to Kathmandu, retracing the spectacular aerial route through the mountains. Upon arrival, transfer to Dwarika's Hotel for a hot shower, comfortable bed, and a day of well-deserved relaxation. Spend the afternoon at leisure — visit the hotel's award-winning spa for a traditional Himalayan treatment, explore the Thamel shopping district for last-minute souvenirs, or simply rest in the beautiful hotel gardens. In the evening, join your group for a farewell dinner at one of Kathmandu's finest restaurants, reflecting on the incredible journey you've shared. Transfer to the airport for your departure flight, carrying memories that will last a lifetime."
}];
function Itinerary() {
  const [openDay, setOpenDay] = useState<number | null>(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-white py-20 sm:py-28" ref={ref} data-id="element-958">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-959">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="mb-12" data-id="element-960">
          <div className="flex items-center gap-3 mb-6" data-id="element-961">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-962">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-963" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-964">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-id="element-965">
            Day-to-Day Itinerary
          </h2>
          <p className="text-slate-500 font-light text-lg" data-id="element-966">
            Your 14-day journey through the Himalayas, step by step
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-12" data-id="element-967">
          {/* Itinerary Accordion */}
          <div className="lg:col-span-3 space-y-3" data-id="element-968">
            {itineraryData.map((day, idx) => <motion.div key={day.day} initial={{
            opacity: 0,
            y: 16
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.4,
            delay: Math.min(idx * 0.04, 0.4)
          }} className="border border-slate-100 rounded-xl overflow-hidden bg-white shadow-sm" data-id="element-969">
                <button onClick={() => setOpenDay(openDay === idx ? null : idx)} className="w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-slate-50/50 transition-colors" aria-expanded={openDay === idx} data-id="element-970">
                  <div className="flex items-center gap-4" data-id="element-971">
                    <span className="w-11 h-11 rounded-full bg-[#0C1F2C] text-[#D4A853] text-sm font-bold flex items-center justify-center flex-shrink-0" data-id="element-972">
                      {day.day}
                    </span>
                    <div data-id="element-973">
                      <p className="text-slate-900 font-semibold text-sm sm:text-base" data-id="element-974">
                        {day.title}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1" data-id="element-975">
                        <span className="text-xs text-slate-400 flex items-center gap-1" data-id="element-976">
                          <ArrowUpIcon className="w-3 h-3" data-id="element-977" />
                          {day.elevation}
                        </span>
                        {day.hours !== '—' && <span className="text-xs text-slate-400 flex items-center gap-1" data-id="element-978">
                            <ClockIcon className="w-3 h-3" data-id="element-979" />
                            {day.hours}
                          </span>}
                        <span className="text-xs text-slate-400 flex items-center gap-1" data-id="element-980">
                          <BedDoubleIcon className="w-3 h-3" data-id="element-981" />
                          {day.hotel}
                        </span>
                      </div>
                    </div>
                  </div>
                  <ChevronDownIcon className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-300 ${openDay === idx ? 'rotate-180' : ''}`} data-id="element-982" />
                </button>
                <AnimatePresence data-id="element-983">
                  {openDay === idx && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: [0.22, 1, 0.36, 1]
              }} className="overflow-hidden" data-id="element-984">
                      <div className="px-5 pb-5 pl-[4.75rem]" data-id="element-985">
                        <p className="text-slate-600 text-sm leading-relaxed mb-3" data-id="element-986">
                          {day.description}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-slate-100" data-id="element-987">
                          <div className="flex items-center gap-2 text-xs text-teal-600 font-medium" data-id="element-988">
                            <UtensilsIcon className="w-3.5 h-3.5" data-id="element-989" />
                            <span data-id="element-990">{day.meals}</span>
                          </div>
                          <span className="text-slate-200" data-id="element-991">|</span>
                          <div className="flex items-center gap-2 text-xs text-slate-500 font-medium" data-id="element-992">
                            <BedDoubleIcon className="w-3.5 h-3.5 text-[#D4A853]" data-id="element-993" />
                            <span data-id="element-994">
                              Overnight:{' '}
                              <span className="text-slate-700" data-id="element-995">
                                {day.hotel}
                              </span>
                            </span>
                          </div>
                          {day.hours !== '—' && <>
                              <span className="text-slate-200" data-id="element-996">|</span>
                              <div className="flex items-center gap-2 text-xs text-slate-500 font-medium" data-id="element-997">
                                <FootprintsIcon className="w-3.5 h-3.5 text-[#D4A853]" data-id="element-998" />
                                <span data-id="element-999">
                                  Walk:{' '}
                                  <span className="text-slate-700" data-id="element-1000">
                                    {day.hours}
                                  </span>
                                </span>
                              </div>
                            </>}
                          <span className="text-slate-200" data-id="element-1001">|</span>
                          <div className="flex items-center gap-2 text-xs text-slate-500 font-medium" data-id="element-1002">
                            <ArrowUpIcon className="w-3.5 h-3.5 text-[#D4A853]" data-id="element-1003" />
                            <span data-id="element-1004">
                              Elevation:{' '}
                              <span className="text-slate-700" data-id="element-1005">
                                {day.elevation}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>

          {/* Route Map Image */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="lg:col-span-2" data-id="element-1006">
            <div className="sticky top-28 space-y-6" data-id="element-1007">
              <div className="rounded-2xl overflow-hidden border border-slate-100 shadow-sm" data-id="element-1008">
                <img src="https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=800&q=80" alt="Everest Base Camp trekking route through the Himalayas" className="w-full aspect-[3/4] object-cover" data-id="element-1009" />
                <div className="p-5 bg-white" data-id="element-1010">
                  <h4 className="font-playfair text-lg font-semibold text-slate-900 mb-2" data-id="element-1011">
                    Trek Route Overview
                  </h4>
                  <p className="text-slate-500 text-sm leading-relaxed" data-id="element-1012">
                    From Lukla (2,860m) through the Khumbu Valley to Everest
                    Base Camp (5,364m) and Kala Patthar (5,545m).
                  </p>
                </div>
              </div>

              {/* Quick booking card */}
              <div className="bg-[#0C1F2C] rounded-2xl p-6 border border-[#D4A853]/15" data-id="element-1013">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1" data-id="element-1014">
                  Starting From
                </p>
                <p className="font-playfair text-2xl font-bold text-white mb-1" data-id="element-1015">
                  $4,500
                </p>
                <p className="text-xs text-slate-500 mb-4" data-id="element-1016">per person</p>
                <a href="#cost" className="block w-full text-center px-6 py-3 bg-teal-600 text-white font-medium rounded-full hover:bg-teal-700 transition-colors shadow-lg shadow-teal-600/20 text-sm" data-id="element-1017">
                  View Pricing & Book
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
/* ═══════════════════════════════════════════
   TOUR COST DETAILS + BOOK NOW
   ═══════════════════════════════════════════ */
function TourCost() {
  const [selectedTier, setSelectedTier] = useState<'5star' | '4star'>('5star');
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const tiers = {
    '5star': {
      label: '5-Star Luxury',
      price: '$4,500',
      includes: ['Airport transfers in luxury vehicle', "5-star hotel in Kathmandu (Dwarika's Hotel)", 'Premium luxury lodges throughout the trek', 'All meals (breakfast, lunch, dinner) — gourmet menu', 'Experienced English-speaking Sherpa guide', 'Porter service (1 porter per 2 trekkers)', 'Domestic flights: Kathmandu–Lukla–Kathmandu', 'All trekking permits and national park fees', 'Welcome and farewell dinners', 'Comprehensive first-aid kit and oxygen', 'Sleeping bag and down jacket rental', 'Satellite phone for emergencies'],
      excludes: ['International flights to/from Kathmandu', 'Nepal visa fees', 'Travel insurance (mandatory)', 'Personal expenses and souvenirs', 'Tips for guides and porters', 'Alcoholic beverages', 'Hot showers on trek (where charged separately)', 'Emergency helicopter evacuation (covered by insurance)']
    },
    '4star': {
      label: '4-Star Premium',
      price: '$3,200',
      includes: ['Airport transfers in comfortable vehicle', '4-star hotel in Kathmandu', 'Best available teahouse lodges on trek', 'All meals (breakfast, lunch, dinner)', 'Experienced English-speaking guide', 'Porter service (1 porter per 2 trekkers)', 'Domestic flights: Kathmandu–Lukla–Kathmandu', 'All trekking permits and national park fees', 'Welcome dinner', 'Basic first-aid kit', 'Sleeping bag rental'],
      excludes: ['International flights to/from Kathmandu', 'Nepal visa fees', 'Travel insurance (mandatory)', 'Personal expenses and souvenirs', 'Tips for guides and porters', 'Alcoholic beverages', 'Hot showers on trek', 'Down jacket rental', 'Satellite phone', 'Emergency helicopter evacuation', 'Farewell dinner']
    }
  };
  const current = tiers[selectedTier];
  return <section id="cost" className="w-full bg-[#0C1F2C] py-20 sm:py-28" ref={ref} data-id="element-1018">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1019">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="text-center mb-12" data-id="element-1020">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-1021">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1022">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1023" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1024">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-white mb-3" data-id="element-1025">
            Tour Cost Details
          </h2>
          <p className="text-slate-400 font-light text-lg" data-id="element-1026">
            Choose the package that suits your style
          </p>
        </motion.div>

        {/* Tier Tabs */}
        <div className="flex justify-center mb-10" data-id="element-1027">
          <div className="inline-flex bg-[#132F3E] rounded-full p-1 border border-white/5" data-id="element-1028">
            <button onClick={() => setSelectedTier('5star')} className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${selectedTier === '5star' ? 'bg-[#D4A853] text-slate-900 shadow-lg' : 'text-slate-400 hover:text-white'}`} data-id="element-1029">
              <StarIcon className="w-4 h-4 inline mr-1.5 -mt-0.5" data-id="element-1030" />
              5-Star Luxury
            </button>
            <button onClick={() => setSelectedTier('4star')} className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${selectedTier === '4star' ? 'bg-[#D4A853] text-slate-900 shadow-lg' : 'text-slate-400 hover:text-white'}`} data-id="element-1031">
              <StarIcon className="w-4 h-4 inline mr-1.5 -mt-0.5" data-id="element-1032" />
              4-Star Premium
            </button>
          </div>
        </div>

        <motion.div key={selectedTier} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} data-id="element-1033">
          {/* Price Header */}
          <div className="text-center mb-10" data-id="element-1034">
            <p className="text-slate-400 text-sm uppercase tracking-wider mb-2" data-id="element-1035">
              {current.label} Package
            </p>
            <p className="font-playfair text-5xl sm:text-6xl font-bold text-white" data-id="element-1036">
              {current.price}
            </p>
            <p className="text-slate-500 text-sm mt-1" data-id="element-1037">per person</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-1038">
            {/* Includes */}
            <div className="bg-[#132F3E] rounded-2xl p-6 sm:p-8 border border-white/5" data-id="element-1039">
              <h3 className="flex items-center gap-2 text-white font-semibold mb-5" data-id="element-1040">
                <CheckIcon className="w-5 h-5 text-teal-400" data-id="element-1041" />
                What's Included
              </h3>
              <ul className="space-y-3" data-id="element-1042">
                {current.includes.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1043">
                    <CheckIcon className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" data-id="element-1044" />
                    <span className="text-slate-300 text-sm" data-id="element-1045">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* Excludes */}
            <div className="bg-[#132F3E] rounded-2xl p-6 sm:p-8 border border-white/5" data-id="element-1046">
              <h3 className="flex items-center gap-2 text-white font-semibold mb-5" data-id="element-1047">
                <XIcon className="w-5 h-5 text-red-400" data-id="element-1048" />
                What's Not Included
              </h3>
              <ul className="space-y-3" data-id="element-1049">
                {current.excludes.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1050">
                    <XIcon className="w-4 h-4 text-red-400/60 mt-0.5 flex-shrink-0" data-id="element-1051" />
                    <span className="text-slate-400 text-sm" data-id="element-1052">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          {/* ── BOOK NOW SECTION ── */}
          <div className="bg-[#132F3E] rounded-2xl p-6 sm:p-10 border border-[#D4A853]/20" data-id="element-1053">
            <div className="text-center mb-8" data-id="element-1054">
              <h3 className="font-playfair text-2xl sm:text-3xl font-bold text-white mb-2" data-id="element-1055">
                Book This Trip
              </h3>
              <p className="text-slate-400 text-sm" data-id="element-1056">
                Fill in your details and we'll get back to you within 24 hours
              </p>
            </div>

            <form onSubmit={e => {
            e.preventDefault();
          }} className="max-w-2xl mx-auto space-y-4" data-id="element-1057">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1058">
                <div data-id="element-1059">
                  <label htmlFor="book-name" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1060">
                    Full Name
                  </label>
                  <div className="relative" data-id="element-1061">
                    <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" data-id="element-1062" />
                    <input id="book-name" type="text" placeholder="Your full name" className="w-full pl-10 pr-4 py-3 bg-[#0C1F2C] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors" data-id="element-1063" />
                  </div>
                </div>
                <div data-id="element-1064">
                  <label htmlFor="book-email" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1065">
                    Email Address
                  </label>
                  <div className="relative" data-id="element-1066">
                    <MailIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" data-id="element-1067" />
                    <input id="book-email" type="email" placeholder="you@example.com" className="w-full pl-10 pr-4 py-3 bg-[#0C1F2C] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors" data-id="element-1068" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1069">
                <div data-id="element-1070">
                  <label htmlFor="book-phone" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1071">
                    Phone Number
                  </label>
                  <div className="relative" data-id="element-1072">
                    <PhoneIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" data-id="element-1073" />
                    <input id="book-phone" type="tel" placeholder="+1 234 567 8900" className="w-full pl-10 pr-4 py-3 bg-[#0C1F2C] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors" data-id="element-1074" />
                  </div>
                </div>
                <div data-id="element-1075">
                  <label htmlFor="book-date" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1076">
                    Preferred Travel Date
                  </label>
                  <div className="relative" data-id="element-1077">
                    <CalendarIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" data-id="element-1078" />
                    <input id="book-date" type="text" placeholder="e.g., October 2026" className="w-full pl-10 pr-4 py-3 bg-[#0C1F2C] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors" data-id="element-1079" />
                  </div>
                </div>
              </div>

              <div data-id="element-1080">
                <label htmlFor="book-message" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1081">
                  Message (Optional)
                </label>
                <div className="relative" data-id="element-1082">
                  <MessageSquareIcon className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" data-id="element-1083" />
                  <textarea id="book-message" rows={3} placeholder="Any special requirements or questions..." className="w-full pl-10 pr-4 py-3 bg-[#0C1F2C] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors resize-none" data-id="element-1084" />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4 pt-2" data-id="element-1085">
                <button type="submit" className="w-full sm:w-auto inline-flex items-center justify-center px-10 py-3.5 bg-teal-600 text-white font-medium rounded-full hover:bg-teal-700 transition-all duration-300 shadow-xl shadow-teal-900/30 text-sm tracking-wide" data-id="element-1086">
                  Book Now — {current.price}
                </button>
                <a href="tel:+9779851032316" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 border border-white/20 text-white font-medium rounded-full hover:bg-white/5 transition-all duration-300 text-sm" data-id="element-1087">
                  <PhoneIcon className="w-4 h-4" data-id="element-1088" />
                  Call Us Instead
                </a>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ═══════════════════════════════════════════
   ACCOMMODATION DETAILS
   ═══════════════════════════════════════════ */
interface HotelSlide {
  name: string;
  location: string;
  image: string;
}
function AccommodationSlider({
  title,
  hotels
}: {
  title: string;
  hotels: HotelSlide[];
}) {
  const [current, setCurrent] = useState(0);
  const prev = () => setCurrent(c => c === 0 ? hotels.length - 1 : c - 1);
  const next = () => setCurrent(c => c === hotels.length - 1 ? 0 : c + 1);
  return <div data-id="element-1089">
      <h3 className="font-playfair text-xl font-semibold text-slate-900 mb-5 flex items-center gap-2" data-id="element-1090">
        <StarIcon className="w-5 h-5 text-[#D4A853] fill-[#D4A853]" data-id="element-1091" />
        {title}
      </h3>
      <div className="relative rounded-2xl overflow-hidden group" data-id="element-1092">
        <AnimatePresence mode="wait" data-id="element-1093">
          <motion.img key={current} src={hotels[current].image} alt={hotels[current].name} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.4
        }} className="w-full aspect-[16/10] object-cover" data-id="element-1094" />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-1095" />
        <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-1096">
          <p className="text-white font-semibold" data-id="element-1097">{hotels[current].name}</p>
          <p className="text-white/70 text-sm flex items-center gap-1" data-id="element-1098">
            <MapPinIcon className="w-3.5 h-3.5" data-id="element-1099" />
            {hotels[current].location}
          </p>
        </div>
        <button onClick={prev} aria-label="Previous hotel" className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors opacity-0 group-hover:opacity-100" data-id="element-1100">
          <ChevronLeftIcon className="w-5 h-5" data-id="element-1101" />
        </button>
        <button onClick={next} aria-label="Next hotel" className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors opacity-0 group-hover:opacity-100" data-id="element-1102">
          <ChevronRightIcon className="w-5 h-5" data-id="element-1103" />
        </button>
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex items-center gap-1.5" data-id="element-1104">
          {hotels.map((_, i) => <button key={i} onClick={() => setCurrent(i)} aria-label={`Go to slide ${i + 1}`} className={`w-2 h-2 rounded-full transition-all duration-300 ${i === current ? 'bg-white w-5' : 'bg-white/40'}`} data-id="element-1105" />)}
        </div>
      </div>
    </div>;
}
function Accommodations() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const fiveStarHotels: HotelSlide[] = [{
    name: "Dwarika's Hotel",
    location: 'Kathmandu',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80'
  }, {
    name: 'Everest View Hotel',
    location: 'Namche Bazaar',
    image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80'
  }, {
    name: 'Yeti Mountain Home',
    location: 'Tengboche',
    image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80'
  }];
  const fourStarHotels: HotelSlide[] = [{
    name: 'Hotel Yak & Yeti',
    location: 'Kathmandu',
    image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&q=80'
  }, {
    name: 'Namche Hotel',
    location: 'Namche Bazaar',
    image: 'https://images.unsplash.com/photo-1445019980597-93fa8acb246c?w=800&q=80'
  }, {
    name: 'Khumbu Lodge',
    location: 'Phakding',
    image: 'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=800&q=80'
  }];
  return <section className="w-full bg-[#FAF8F5] py-20 sm:py-28" ref={ref} data-id="element-1106">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1107">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="mb-12" data-id="element-1108">
          <div className="flex items-center gap-3 mb-6" data-id="element-1109">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1110">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1111" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1112">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-id="element-1113">
            Accommodation Details
          </h2>
          <p className="text-slate-500 font-light text-lg" data-id="element-1114">
            Where you'll rest after each day's adventure
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1115">
          <AccommodationSlider title="5-Star Luxury Hotels" hotels={fiveStarHotels} data-id="element-1116" />
          <AccommodationSlider title="4-Star Premium Hotels" hotels={fourStarHotels} data-id="element-1117" />
        </div>
      </div>
    </section>;
}
/* ═══════════════════════════════════════════
   USEFUL INFORMATION
   ═══════════════════════════════════════════ */
function UsefulInfo() {
  const [activeTab, setActiveTab] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const tabs = [{
    label: 'Best Time to Visit',
    icon: <SunIcon className="w-4 h-4" data-id="element-1118" />,
    content: <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-1119">
          <p data-id="element-1120">
            The best months for the Everest Base Camp trek are{' '}
            <strong className="text-slate-800" data-id="element-1121">September to November</strong>{' '}
            (autumn) and{' '}
            <strong className="text-slate-800" data-id="element-1122">March to May</strong> (spring).
            These seasons offer the clearest skies, most stable weather, and
            best mountain visibility.
          </p>
          <p data-id="element-1123">
            <strong className="text-slate-800" data-id="element-1124">Autumn (Sep–Nov):</strong> The
            most popular season. Expect clear blue skies, excellent visibility,
            and comfortable daytime temperatures. Nights can be cold at higher
            altitudes (-10°C to -15°C).
          </p>
          <p data-id="element-1125">
            <strong className="text-slate-800" data-id="element-1126">Spring (Mar–May):</strong> Warmer
            temperatures and blooming rhododendrons make this a beautiful time
            to trek. Slightly more cloud cover than autumn but still excellent
            conditions.
          </p>
          <p data-id="element-1127">
            <strong className="text-slate-800" data-id="element-1128">Winter (Dec–Feb):</strong>{' '}
            Possible but very cold with shorter days. Fewer trekkers on the
            trail. Some lodges may be closed.
          </p>
        </div>
  }, {
    label: 'Level of Difficulty',
    icon: <MountainSnowIcon className="w-4 h-4" data-id="element-1129" />,
    content: <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-1130">
          <p data-id="element-1131">
            This trek is rated{' '}
            <strong className="text-slate-800" data-id="element-1132">Moderate to Challenging</strong>.
            While no technical climbing skills are required, the high altitude
            and daily walking hours demand reasonable physical fitness.
          </p>
          <p data-id="element-1133">
            <strong className="text-slate-800" data-id="element-1134">Physical Requirements:</strong>{' '}
            You should be able to walk 5–7 hours per day on uneven terrain with
            elevation gains of 500–800 meters. Regular cardiovascular exercise
            (hiking, running, cycling) for 2–3 months before the trek is
            recommended.
          </p>
          <p data-id="element-1135">
            <strong className="text-slate-800" data-id="element-1136">Altitude:</strong> The maximum
            altitude is 5,364m at Everest Base Camp (5,545m at Kala Patthar).
            Two acclimatization days are built into the itinerary. Our guides
            monitor your health daily and carry supplemental oxygen.
          </p>
          <p data-id="element-1137">
            <strong className="text-slate-800" data-id="element-1138">Terrain:</strong>{' '}
            Well-established trails with some steep sections. Stone steps,
            suspension bridges, and rocky paths are common. Trekking poles are
            recommended.
          </p>
        </div>
  }, {
    label: 'Meals & Accommodation',
    icon: <UtensilsIcon className="w-4 h-4" data-id="element-1139" />,
    content: <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-1140">
          <p data-id="element-1141">
            <strong className="text-slate-800" data-id="element-1142">Meals:</strong> All meals are
            included throughout the trek. In Kathmandu, enjoy fine dining at
            your hotel. On the trail, our team prepares a mix of Nepali,
            Continental, and Asian cuisine using the freshest available
            ingredients.
          </p>
          <p data-id="element-1143">
            Breakfast typically includes eggs, porridge, toast, pancakes, and
            hot beverages. Lunch features soups, pasta, rice dishes, and local
            dal bhat. Dinner is a multi-course affair with soup, main course,
            and dessert. Vegetarian and dietary requirements are accommodated
            with advance notice.
          </p>
          <p data-id="element-1144">
            <strong className="text-slate-800" data-id="element-1145">Accommodation:</strong> In
            Kathmandu, you'll stay at Dwarika's Hotel (5-star) or Hotel Yak &
            Yeti (4-star). On the trek, we use the best available lodges at each
            stop — private rooms with attached bathrooms where available, heated
            dining areas, and comfortable bedding.
          </p>
          <p data-id="element-1146">
            Hot showers are available at most stops (some may have a small
            charge at higher altitudes). Charging facilities for electronics are
            available at all lodges.
          </p>
        </div>
  }];
  return <section className="w-full bg-white py-20 sm:py-28" ref={ref} data-id="element-1147">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1148">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="mb-10" data-id="element-1149">
          <div className="flex items-center gap-3 mb-6" data-id="element-1150">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1151">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1152" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1153">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-id="element-1154">
            Useful Information
          </h2>
        </motion.div>

        <div className="border-b border-slate-200 mb-8" data-id="element-1155">
          <div className="flex gap-0 overflow-x-auto" data-id="element-1156">
            {tabs.map((tab, i) => <button key={i} onClick={() => setActiveTab(i)} className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-300 ${activeTab === i ? 'border-teal-600 text-teal-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-1157">
                {tab.icon}
                {tab.label}
              </button>)}
          </div>
        </div>

        <AnimatePresence mode="wait" data-id="element-1158">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.3
        }} className="max-w-3xl" data-id="element-1159">
            {tabs[activeTab].content}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}
/* ═══════════════════════════════════════════
   FAQ SECTION
   ═══════════════════════════════════════════ */
const faqData = [{
  q: 'How fit do I need to be for this trek?',
  a: 'You should have a good level of cardiovascular fitness. We recommend training for at least 2–3 months before the trek with regular hiking, running, or cycling. You should be comfortable walking 5–7 hours per day on varied terrain.'
}, {
  q: 'Is altitude sickness a concern?',
  a: 'Altitude sickness is a real consideration above 3,000m. Our itinerary includes two acclimatization days, and our guides are trained in altitude sickness recognition and management. We carry supplemental oxygen and a portable altitude chamber. We strongly recommend travel insurance that covers emergency helicopter evacuation.'
}, {
  q: 'What is the accommodation like on the trek?',
  a: "We use the best available lodges at each stop. In the 5-star package, you'll stay at premium lodges with private rooms, attached bathrooms (where available), heated dining areas, and comfortable bedding. The 4-star package uses the best standard teahouses with clean, comfortable rooms."
}, {
  q: 'Can I charge my electronic devices?',
  a: 'Yes, charging facilities are available at all lodges, though some may charge a small fee at higher altitudes. We recommend bringing a portable power bank as backup. Solar chargers can also be useful.'
}, {
  q: 'What happens if the Lukla flight is delayed?',
  a: 'Weather-related flight delays to Lukla are common. We build buffer days into our schedule and have contingency plans including helicopter transfers if needed. Our team monitors weather conditions closely and will keep you informed of any changes.'
}, {
  q: 'Is travel insurance required?',
  a: 'Yes, comprehensive travel insurance is mandatory for this trek. Your policy must cover trekking up to 6,000m, emergency helicopter evacuation, and trip cancellation. We can recommend suitable insurance providers.'
}, {
  q: 'Can dietary requirements be accommodated?',
  a: 'Absolutely. We can accommodate vegetarian, vegan, gluten-free, and other dietary requirements with advance notice. Our chefs are experienced in preparing diverse menus even at high altitude.'
}];
function FAQ() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-[#FAF8F5] py-20 sm:py-28" ref={ref} data-id="element-1160">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1161">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="text-center mb-12" data-id="element-1162">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-1163">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1164">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1165" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1166">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-id="element-1167">
            Frequently Asked Questions
          </h2>
          <p className="text-slate-500 font-light text-lg" data-id="element-1168">
            Everything you need to know before you go
          </p>
        </motion.div>

        <div className="space-y-3" data-id="element-1169">
          {faqData.map((faq, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 12
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: Math.min(idx * 0.06, 0.3)
        }} className="bg-white rounded-xl border border-[#D4A853]/10 overflow-hidden" data-id="element-1170">
              <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50/50 transition-colors" aria-expanded={openFaq === idx} data-id="element-1171">
                <span className="flex items-center gap-3" data-id="element-1172">
                  <HelpCircleIcon className="w-5 h-5 text-teal-600 flex-shrink-0" data-id="element-1173" />
                  <span className="text-slate-900 font-medium text-sm sm:text-base" data-id="element-1174">
                    {faq.q}
                  </span>
                </span>
                <ChevronDownIcon className={`w-5 h-5 text-slate-400 flex-shrink-0 ml-4 transition-transform duration-300 ${openFaq === idx ? 'rotate-180' : ''}`} data-id="element-1175" />
              </button>
              <AnimatePresence data-id="element-1176">
                {openFaq === idx && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: [0.22, 1, 0.36, 1]
            }} className="overflow-hidden" data-id="element-1177">
                    <div className="px-5 pb-5 pl-[3.25rem]" data-id="element-1178">
                      <p className="text-slate-600 text-sm leading-relaxed" data-id="element-1179">
                        {faq.a}
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
/* ═══════════════════════════════════════════
   BOOK THIS TRIP CTA
   ═══════════════════════════════════════════ */
function BookCTA({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <section className="relative w-full overflow-hidden" data-id="element-1180">
      <div className="absolute inset-0" data-id="element-1181">
        <img src="https://images.unsplash.com/photo-1585409677983-0f6c41ca9c3b?w=1920&q=80" alt="Annapurna mountain range at golden hour" className="w-full h-full object-cover" data-id="element-1182" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" data-id="element-1183" />
      </div>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-24 sm:py-32 text-center" data-id="element-1184">
        <span className="inline-block text-[#D4A853] text-sm font-medium tracking-[0.3em] uppercase mb-5" data-id="element-1185">
          Explore More
        </span>
        <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight mb-6" data-id="element-1186">
          Discover Our Other
          <br data-id="element-1187" />
          <span className="italic font-medium" data-id="element-1188">Luxury Adventures</span>
        </h2>
        <p className="text-white/70 text-lg font-light max-w-2xl mx-auto mb-10" data-id="element-1189">
          From the Annapurna Circuit to Chitwan Safari, explore our full range
          of premium Nepal experiences.
        </p>
        <button onClick={() => onNavigate('home')} className="inline-flex items-center px-8 py-4 bg-teal-600 text-white font-medium rounded-full hover:bg-teal-700 transition-all duration-300 shadow-xl shadow-teal-900/30 text-sm tracking-wide" data-id="element-1190">
          View All Trips
        </button>
      </div>
    </section>;
}
/* ═══════════════════════════════════════════
   BOOK THIS TRIP FORM
   ═══════════════════════════════════════════ */
function BookTripForm() {
  const [selectedTier, setSelectedTier] = useState<'5star' | '4star'>('5star');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    message: ''
  });
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const tiers = {
    '5star': {
      label: '5-Star Luxury',
      price: '$4,500',
      includes: ['Airport transfers in luxury vehicle', "5-star hotel in Kathmandu (Dwarika's Hotel)", 'Premium luxury lodges throughout the trek', 'All meals (breakfast, lunch, dinner) — gourmet menu', 'Experienced English-speaking Sherpa guide', 'Porter service (1 porter per 2 trekkers)', 'Domestic flights: Kathmandu–Lukla–Kathmandu', 'All trekking permits and national park fees', 'Welcome and farewell dinners', 'Comprehensive first-aid kit and oxygen', 'Sleeping bag and down jacket rental', 'Satellite phone for emergencies'],
      excludes: ['International flights to/from Kathmandu', 'Nepal visa fees', 'Travel insurance (mandatory)', 'Personal expenses and souvenirs', 'Tips for guides and porters', 'Alcoholic beverages', 'Hot showers on trek (where charged separately)', 'Emergency helicopter evacuation (covered by insurance)']
    },
    '4star': {
      label: '4-Star Premium',
      price: '$3,200',
      includes: ['Airport transfers in comfortable vehicle', '4-star hotel in Kathmandu', 'Best available teahouse lodges on trek', 'All meals (breakfast, lunch, dinner)', 'Experienced English-speaking guide', 'Porter service (1 porter per 2 trekkers)', 'Domestic flights: Kathmandu–Lukla–Kathmandu', 'All trekking permits and national park fees', 'Welcome dinner', 'Basic first-aid kit', 'Sleeping bag rental'],
      excludes: ['International flights to/from Kathmandu', 'Nepal visa fees', 'Travel insurance (mandatory)', 'Personal expenses and souvenirs', 'Tips for guides and porters', 'Alcoholic beverages', 'Hot showers on trek', 'Down jacket rental', 'Satellite phone', 'Emergency helicopter evacuation', 'Farewell dinner']
    }
  };
  const current = tiers[selectedTier];
  const validateForm = () => {
    const newErrors = {
      name: '',
      email: '',
      phone: '',
      date: '',
      message: ''
    };
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[1-9]\d{1,14}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.date.trim()) {
      newErrors.date = 'Preferred travel date is required';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    setErrors(newErrors);
    return Object.values(newErrors).every(error => !error);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          date: '',
          message: ''
        });
        setErrors({
          name: '',
          email: '',
          phone: '',
          date: '',
          message: ''
        });
      }, 3000);
    }
  };
  return <section className="w-full bg-[#FAF8F5] py-20 sm:py-28" ref={ref} data-id="element-1191">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1192">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="text-center mb-12" data-id="element-1193">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-1194">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1195">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1196" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1197">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-id="element-1198">
            Book This Trip
          </h2>
          <p className="text-slate-500 font-light text-lg" data-id="element-1199">
            Fill in your details and we'll get back to you within 24 hours
          </p>
        </motion.div>

        <div className="space-y-6" data-id="element-1200">
          <div className="flex justify-center mb-6" data-id="element-1201">
            <div className="inline-flex bg-[#132F3E] rounded-full p-1 border border-white/5" data-id="element-1202">
              <button onClick={() => setSelectedTier('5star')} className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${selectedTier === '5star' ? 'bg-[#D4A853] text-slate-900 shadow-lg' : 'text-slate-400 hover:text-white'}`} data-id="element-1203">
                <StarIcon className="w-4 h-4 inline mr-1.5 -mt-0.5" data-id="element-1204" />
                5-Star Luxury
              </button>
              <button onClick={() => setSelectedTier('4star')} className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${selectedTier === '4star' ? 'bg-[#D4A853] text-slate-900 shadow-lg' : 'text-slate-400 hover:text-white'}`} data-id="element-1205">
                <StarIcon className="w-4 h-4 inline mr-1.5 -mt-0.5" data-id="element-1206" />
                4-Star Premium
              </button>
            </div>
          </div>

          <div className="text-center mb-6" data-id="element-1207">
            <p className="text-slate-400 text-sm uppercase tracking-wider mb-2" data-id="element-1208">
              {current.label} Package
            </p>
            <p className="font-playfair text-5xl sm:text-6xl font-bold text-white" data-id="element-1209">
              {current.price}
            </p>
            <p className="text-slate-500 text-sm mt-1" data-id="element-1210">per person</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1211">
            <div data-id="element-1212">
              <label htmlFor="book-name" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1213">
                Full Name
              </label>
              <div className="relative" data-id="element-1214">
                <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" data-id="element-1215" />
                <input id="book-name" type="text" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} placeholder="Your full name" className={`w-full pl-10 pr-4 py-3 bg-[#0C1F2C] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors ${errors.name ? 'border-red-500' : ''}`} data-id="element-1216" />
                {errors.name && <p className="text-red-500 text-xs mt-1" data-id="element-1217">{errors.name}</p>}
              </div>
            </div>
            <div data-id="element-1218">
              <label htmlFor="book-email" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1219">
                Email Address
              </label>
              <div className="relative" data-id="element-1220">
                <MailIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" data-id="element-1221" />
                <input id="book-email" type="email" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} placeholder="you@example.com" className={`w-full pl-10 pr-4 py-3 bg-[#0C1F2C] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors ${errors.email ? 'border-red-500' : ''}`} data-id="element-1222" />
                {errors.email && <p className="text-red-500 text-xs mt-1" data-id="element-1223">{errors.email}</p>}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1224">
            <div data-id="element-1225">
              <label htmlFor="book-phone" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1226">
                Phone Number
              </label>
              <div className="relative" data-id="element-1227">
                <PhoneIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" data-id="element-1228" />
                <input id="book-phone" type="tel" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} placeholder="+1 234 567 8900" className={`w-full pl-10 pr-4 py-3 bg-[#0C1F2C] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors ${errors.phone ? 'border-red-500' : ''}`} data-id="element-1229" />
                {errors.phone && <p className="text-red-500 text-xs mt-1" data-id="element-1230">{errors.phone}</p>}
              </div>
            </div>
            <div data-id="element-1231">
              <label htmlFor="book-date" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1232">
                Preferred Travel Date
              </label>
              <div className="relative" data-id="element-1233">
                <CalendarIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" data-id="element-1234" />
                <input id="book-date" type="text" value={formData.date} onChange={e => setFormData({
                ...formData,
                date: e.target.value
              })} placeholder="e.g., October 2026" className={`w-full pl-10 pr-4 py-3 bg-[#0C1F2C] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors ${errors.date ? 'border-red-500' : ''}`} data-id="element-1235" />
                {errors.date && <p className="text-red-500 text-xs mt-1" data-id="element-1236">{errors.date}</p>}
              </div>
            </div>
          </div>

          <div data-id="element-1237">
            <label htmlFor="book-message" className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-1238">
              Message (Optional)
            </label>
            <div className="relative" data-id="element-1239">
              <MessageSquareIcon className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" data-id="element-1240" />
              <textarea id="book-message" value={formData.message} onChange={e => setFormData({
              ...formData,
              message: e.target.value
            })} rows={3} placeholder="Any special requirements or questions..." className={`w-full pl-10 pr-4 py-3 bg-[#0C1F2C] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4A853]/50 transition-colors resize-none ${errors.message ? 'border-red-500' : ''}`} data-id="element-1241" />
              {errors.message && <p className="text-red-500 text-xs mt-1" data-id="element-1242">{errors.message}</p>}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 pt-2" data-id="element-1243">
            <button type="button" onClick={handleSubmit} className="w-full sm:w-auto inline-flex items-center justify-center px-10 py-3.5 bg-teal-600 text-white font-medium rounded-full hover:bg-teal-700 transition-all duration-300 shadow-xl shadow-teal-900/30 text-sm tracking-wide" data-id="element-1244">
              {isSubmitted ? 'Submitted!' : 'Book Now — ' + current.price}
            </button>
            <a href="tel:+9779851032316" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 border border-white/20 text-white font-medium rounded-full hover:bg-white/5 transition-all duration-300 text-sm" data-id="element-1245">
              <PhoneIcon className="w-4 h-4" data-id="element-1246" />
              Call Us Instead
            </a>
          </div>
        </div>
      </div>
    </section>;
}
/* ═══════════════════════════════════════════
   MAIN PAGE COMPONENT
   ═══════════════════════════════════════════ */
export function EverestTrekPage({
  onNavigate
}: EverestTrekPageProps) {
  return <div className="w-full" data-id="element-1247">
      <TourHero onNavigate={onNavigate} data-id="element-1248" />
      <TripOverview data-id="element-1249" />
      <TripHighlights data-id="element-1250" />
      <Itinerary data-id="element-1251" />
      <TourCost data-id="element-1252" />
      <Accommodations data-id="element-1253" />
      <UsefulInfo data-id="element-1254" />
      <FAQ data-id="element-1255" />
      <BookCTA onNavigate={onNavigate} data-id="element-1256" />
    </div>;
}